'use client';

import * as DS from '@aurorasolar/ds';
import { x } from '@xstyled/styled-components';
import { useEffect, useReducer } from 'react';

import { useTracking } from '@/analytics';
import { TRACKING } from '@/helpers/tracking';

import { Dollhouse, GroupChat, PhonePanel, PrepShop, Scorecard } from './components';
import {
  ApplianceId,
  computeScore,
  createInitialState,
  gameReducer,
  GameState,
  PrepConfig,
  SECONDS_PER_GAME_HOUR,
} from './engine';

const TICK_INTERVAL_MS = 250;
const minutesPerTick = (speed: GameState['speed']): number =>
  (60 / SECONDS_PER_GAME_HOUR) * speed * (TICK_INTERVAL_MS / 1000);

export const BlackoutBattle = () => {
  const [state, dispatch] = useReducer(gameReducer, undefined, createInitialState);
  const { trackEvent } = useTracking();

  useEffect(() => {
    if (state.phase !== 'playing') return undefined;
    const interval = setInterval(
      () => dispatch({ deltaMinutes: minutesPerTick(state.speed), type: 'tick' }),
      TICK_INTERVAL_MS
    );
    return () => clearInterval(interval);
  }, [state.phase, state.speed]);

  useEffect(() => {
    if (state.phase !== 'scorecard') return;
    const score = computeScore(state);
    trackEvent(TRACKING.BLACKOUT_BATTLE_COMPLETED_GAME, {
      scenario: state.prep.scenarioId,
      score: score.total,
      title: score.title,
    });
    // state stops changing once the scorecard is reached, so this fires exactly once per game
  }, [state, trackEvent]);

  const handleStart = (prep: PrepConfig) => {
    trackEvent(TRACKING.BLACKOUT_BATTLE_STARTED_GAME, { difficulty: prep.difficultyId, scenario: prep.scenarioId });
    dispatch({ prep, type: 'start' });
  };

  const handleRespond = (responseIndex: number) => {
    const event = state.pendingEvent;
    if (event) {
      const choice = event.responses[responseIndex]?.label ?? '';
      trackEvent(TRACKING.BLACKOUT_BATTLE_ANSWERED_PROMPT, { choice, event: event.id });
    }
    dispatch({ responseIndex, type: 'respond' });
  };

  const handleToggleLoad = (applianceId: ApplianceId) => dispatch({ applianceId, type: 'toggleLoad' });

  return (
    <x.section maxW="1080px" minH="calc(100dvh - 80px)" mx="auto" px={{ _: 4, md: 6 }} py={{ _: 5, md: 8 }}>
      {state.phase === 'prep' && (
        <DS.Flex gap={5}>
          <x.div textAlign="center">
            <DS.Text as="h1" text={{ _: 'h2', md: 'd2' }}>
              Blackout Battle
            </DS.Text>
            <DS.Text as="p" color="uiHelperCopy" mt={2} text="body16">
              The grid goes down at noon. Outfit your home, manage the family group chat, and keep the lights on until
              power returns.
            </DS.Text>
          </x.div>
          <PrepShop onStart={handleStart} />
        </DS.Flex>
      )}

      {state.phase === 'playing' && (
        <x.div display="grid" gap={4} gridTemplateColumns={{ _: '1fr', md: '1.2fr 1fr' }}>
          <DS.Flex gap={4}>
            <Dollhouse state={state} />
            <PhonePanel
              onRestart={() => dispatch({ type: 'restart' })}
              onSetSpeed={(speed) => dispatch({ speed, type: 'setSpeed' })}
              onToggleLoad={handleToggleLoad}
              state={state}
            />
          </DS.Flex>
          <x.div h={{ _: '420px', md: 'auto' }} maxH={{ md: '700px' }}>
            <GroupChat chatLog={state.chatLog} onRespond={handleRespond} pendingEvent={state.pendingEvent} />
          </x.div>
        </x.div>
      )}

      {state.phase === 'scorecard' && <Scorecard onRestart={() => dispatch({ type: 'restart' })} state={state} />}
    </x.section>
  );
};
